/**
 * Ce que le prof écrit en rouge sous une réponse, sur la feuille (règles pures).
 * Remplace la popup de résultat du mode « sur la feuille ».
 */

export interface TeacherNoteInput {
  wasCorrect: boolean;
  /** bonnes réponses d'affilée APRÈS cette réponse (0 si fausse) */
  streak?: number;
  /** points gagnés (XP, ou points d'un live) */
  gain?: number;
  /** unité des points (« XP » par défaut) */
  unit?: string;
  /** bonus compris dans le gain */
  streakBonus?: number;
  timeBonus?: number;
}

/** L'appréciation du prof, courte. */
export function teacherRemark({ wasCorrect, streak = 0 }: TeacherNoteInput): string {
  if (!wasCorrect) return 'Attention !';
  if (streak >= 5) return `Excellent, ${streak} de suite !`;
  if (streak >= 3) return `Très bien, ${streak} de suite !`;
  if (streak === 2) return 'Très bien !';
  return 'Bien !';
}

/** « +120 XP » (rien pour une réponse fausse sans point). */
export function teacherGain({
  wasCorrect,
  gain = 0,
  unit = 'XP',
}: TeacherNoteInput): string | null {
  if (gain > 0) return `+${gain} ${unit}`;
  return wasCorrect ? `+0 ${unit}` : null;
}

/** « dont série +50 · vitesse +30 » (rien sans bonus). */
export function teacherBonuses({
  streakBonus = 0,
  timeBonus = 0,
}: TeacherNoteInput): string | null {
  const parts: string[] = [];
  if (streakBonus > 0) parts.push(`série +${streakBonus}`);
  if (timeBonus > 0) parts.push(`vitesse +${timeBonus}`);
  return parts.length ? `dont ${parts.join(' · ')}` : null;
}

/**
 * Appréciation d'une copie entière, d'après la note sur 20 : ce que le prof
 * écrit à côté de la note finale.
 */
export function teacherAppreciation(scoreOn20: number): string {
  if (scoreOn20 >= 18) return 'Excellent travail !';
  if (scoreOn20 >= 15) return 'Très bien !';
  if (scoreOn20 >= 12) return 'Bien, continue !';
  if (scoreOn20 >= 10) return 'Assez bien, revois tes erreurs.';
  return 'Revois la correction et recommence.';
}

/** Note sur 20 d'après le nombre de bonnes réponses (arrondie au demi-point). */
export function gradeOn20(correct: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((correct / total) * 40) / 2;
}

/** « 14,5 » (virgule française, pas de « ,0 »). */
export function formatGrade(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
}

/** Pourcentage de bonnes réponses, arrondi à l'unité (0 sans question). */
export function scorePercent(correct: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((Math.min(correct, total) / total) * 100);
}

export type TeacherInk = 'good' | 'mid' | 'bad';

/**
 * La couleur de l'encre du prof d'après le score : vert de 80 à 100 %, orange de
 * 50 à 79 %, rouge sous 50 % (une bonne réponse = 100 %, une fausse = 0 %).
 */
export function teacherInk(percent: number): TeacherInk {
  if (percent >= 80) return 'good';
  if (percent >= 50) return 'mid';
  return 'bad';
}
